import type { SupabaseClient } from '@supabase/supabase-js'
import type { Movimiento, MovimientoConCliente, Cuenta, Database } from '@/types/database'
import { formatDate, formatearCUIT } from '@/lib/utils'
import { calcularSaldosAcumulados, calcularTotales } from '@/lib/movimientos-utils'
import { documentosService } from '@/core/services/documentosService'
import { fetchAllPages, fetchInChunks } from '@/lib/postgrest-chunks'
import { cuentaOrgFilter } from '@/services/cuentasService'
import type { AplicacionPago } from '@/services/aplicacionesService'

export interface FiltrosMovimientos {
  clienteId?: string
  tipo?: 'Deuda' | 'Pago' | 'Gasto'
  cuentaId?: string
  fechaDesde?: string
  fechaHasta?: string
  mesAnio?: string
  /** Filtra por estado de pago (solo aplica a gastos internos). */
  estadoPago?: 'pagado' | 'pendiente'
}

export interface ExportContext {
  cuentasMap?: Map<string, string>
  serviciosMap?: Map<string, string>
  // Estado de cuenta: saldo neto (deudas/pagos parciales por su pendiente),
  // igual que el footer del dialog y el PDF (ORR-108). Default (página
  // Movimientos): saldo bruto debe - haber.
  neto?: boolean
}

type MovimientoInsert = Database['public']['Tables']['movimientos']['Insert']
type MovimientoUpdate = Database['public']['Tables']['movimientos']['Update']


/**
 * Servicio para gestión de movimientos.
 *
 * IMPORTANTE: Todos los métodos reciben el cliente Supabase autenticado como primer parámetro.
 * Usar con useSupabase() desde el hook que llame a estos métodos.
 *
 * @example
 * const { supabase } = useSupabase()
 * const movimientos = await movimientosService.listar(supabase!, organizacionId, filtros)
 */
export const movimientosService = {
  async listar(
    client: SupabaseClient<Database>,
    organizacionId: string,
    filtros?: FiltrosMovimientos,
  ): Promise<MovimientoConCliente[]> {
    // RLS no separa organizaciones: sin organización la consulta traería las de todas.
    if (!organizacionId) throw new Error('No organization')
    let movimientos = await fetchAllPages<MovimientoConCliente>(async (desde, hasta) => {
      let query = client
        .from('movimientos')
        .select(`
          *,
          cliente:clientes!cliente_id(id, persona:personas(nombre_completo, cuit))
        `)
        .eq('organizacion_id', organizacionId)
        .order('fecha', { ascending: false })
        .order('created_at', { ascending: false })
        // Desempate por PK: sin él, dos páginas de `.range()` pueden repetir o saltear filas.
        .order('id')

      if (filtros?.clienteId) {
        query = query.eq('cliente_id', filtros.clienteId)
      }
      if (filtros?.tipo) {
        query = query.eq('tipo', filtros.tipo)
      } else {
        // Excluir gastos internos del estudio por defecto: las grillas de clientes
        // (Deuda/Pago) no deben mezclarse con los gastos del estudio. Para listarlos
        // hay que pasar explícitamente { tipo: 'Gasto' }.
        query = query.neq('tipo', 'Gasto')
      }
      if (filtros?.cuentaId) {
        query = query.eq('cuenta_id', filtros.cuentaId)
      }
      if (filtros?.fechaDesde) {
        query = query.gte('fecha', filtros.fechaDesde)
      }
      if (filtros?.fechaHasta) {
        query = query.lte('fecha', filtros.fechaHasta)
      }
      if (filtros?.mesAnio) {
        query = query.eq('mes_anio', filtros.mesAnio)
      }
      if (filtros?.estadoPago) {
        query = query.eq('estado_pago', filtros.estadoPago)
      }

      const { data, error } = await query.range(desde, hasta)
      if (error) throw error
      return (data ?? []) as MovimientoConCliente[]
    })

    // Enrich with document attachment info from documento_vinculaciones
    if (movimientos.length > 0) {
      const ids = movimientos.map(m => m.id)
      const vinculaciones = await fetchInChunks(ids, async (batch) => {
        const { data, error } = await client
          .from('documento_vinculaciones')
          .select('entidad_id, documento_id')
          .eq('entidad_tipo', 'movimiento')
          .eq('workspace', 'contable')
          .eq('rol', 'comprobante')
          .in('entidad_id', batch)
        if (error) throw error
        return data ?? []
      })

      if (vinculaciones.length > 0) {
        const adjuntoMap = new Map(vinculaciones.map(v => [v.entidad_id, v.documento_id]))
        movimientos = movimientos.map(m => ({
          ...m,
          _adjuntoDocumentoId: adjuntoMap.get(m.id) ?? null,
        }))
      }

      // Enrich with aplicacion status from saldos views
      const deudaIds = movimientos.filter(m => m.tipo === 'Deuda').map(m => m.id)
      const pagoIds = movimientos.filter(m => m.tipo === 'Pago').map(m => m.id)

      type AplicacionEstado = NonNullable<MovimientoConCliente['_aplicacionEstado']>
      const aplicacionMap = new Map<string, { estado: AplicacionEstado; monto_aplicado: number; saldo: number }>()

      const [deudas, pagos] = await Promise.all([
        fetchInChunks(deudaIds, async (batch) => {
          const { data, error } = await client
            .from('vista_saldos_deudas')
            .select('deuda_id, estado, monto_aplicado, saldo_pendiente')
            .in('deuda_id', batch)
          if (error) throw error
          return data ?? []
        }),
        fetchInChunks(pagoIds, async (batch) => {
          const { data, error } = await client
            .from('vista_saldos_pagos')
            .select('pago_id, estado, monto_aplicado, saldo_a_favor')
            .in('pago_id', batch)
          if (error) throw error
          return data ?? []
        }),
      ])

      for (const d of deudas) {
        if (d.deuda_id) {
          aplicacionMap.set(d.deuda_id, {
            estado: (d.estado as AplicacionEstado) ?? 'pendiente',
            monto_aplicado: d.monto_aplicado ?? 0,
            saldo: d.saldo_pendiente ?? 0,
          })
        }
      }
      for (const p of pagos) {
        if (p.pago_id) {
          aplicacionMap.set(p.pago_id, {
            estado: (p.estado as AplicacionEstado) ?? 'sin_aplicar',
            monto_aplicado: p.monto_aplicado ?? 0,
            saldo: p.saldo_a_favor ?? 0,
          })
        }
      }

      if (aplicacionMap.size > 0) {
        movimientos = movimientos.map(m => {
          const info = aplicacionMap.get(m.id)
          if (!info) return m
          return {
            ...m,
            _aplicacionEstado: info.estado,
            _aplicacionMontoAplicado: info.monto_aplicado,
            _aplicacionSaldoPendiente: info.saldo,
          }
        })
      }
    }

    return movimientos
  },

  async obtenerPorId(client: SupabaseClient<Database>, id: string): Promise<Movimiento> {
    const { data, error } = await client
      .from('movimientos')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data as Movimiento
  },

  async crear(client: SupabaseClient<Database>, movimiento: MovimientoInsert): Promise<Movimiento> {
    const { data, error } = await client
      .from('movimientos')
      .insert(movimiento)
      .select()
      .single()

    if (error) throw error
    return data as Movimiento
  },

  async actualizar(client: SupabaseClient<Database>, id: string, movimiento: MovimientoUpdate): Promise<Movimiento> {
    const { data, error } = await client
      .from('movimientos')
      .update(movimiento)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Movimiento
  },

  /**
   * Actualiza un gasto interno del estudio. A diferencia de `actualizar`, restringe el
   * UPDATE a filas `tipo='Gasto'` de la organización dada — evita que el hook de gastos
   * pueda tocar una Deuda/Pago de cliente o un movimiento de otra org (RLS solo valida
   * `role='authenticated'`, así que el aislamiento es app-layer).
   */
  async actualizarGasto(
    client: SupabaseClient<Database>,
    organizacionId: string,
    id: string,
    movimiento: MovimientoUpdate,
  ): Promise<Movimiento> {
    const { data, error } = await client
      .from('movimientos')
      .update(movimiento)
      .eq('id', id)
      .eq('organizacion_id', organizacionId)
      .eq('tipo', 'Gasto')
      // Las transferencias son inmutables (ORR-133): editar una pata desincronizaría el par.
      .eq('es_transferencia', false)
      .select()
      .single()

    if (error) throw error
    return data as Movimiento
  },

  async eliminar(client: SupabaseClient<Database>, id: string): Promise<void> {
    // ORR-218: capturar los comprobantes adjuntos (con su archivo_path) ANTES del borrado.
    // La RPC `eliminar_movimiento_seguro` elimina server-side la fila `documentos` de cada
    // comprobante (autorizada por `eliminarMovimiento`, así lo limpian TODOS los roles que
    // borran movimientos, incluido contador_asociado sin `eliminarDocumento`), pero SQL no
    // accede al bucket → los archivos de Storage se borran acá.
    const comprobantes = await documentosService.obtenerPorEntidad(client, 'movimiento', id, 'comprobante')

    // RPC con RBAC: verifica rol del caller antes de eliminar.
    // Si el movimiento es pata de una transferencia, la RPC elimina el par completo (ORR-133).
    const { error } = await client.rpc('eliminar_movimiento_seguro', { p_id: id })
    if (error) throw error

    // Best-effort: borrar los archivos de Storage de los comprobantes (la fila ya la borró la RPC).
    // Un fallo acá no debe revertir el borrado ya confirmado del movimiento.
    for (const comprobante of comprobantes) {
      try {
        await documentosService.eliminarArchivo(client, comprobante.archivo_path)
      } catch (err) {
        console.error(`[ORR-218] no se pudo eliminar archivo de comprobante ${comprobante.id}:`, err)
      }
    }
  },

  /**
   * Crea una transferencia entre cuentas propias como par atómico de movimientos
   * vinculados (egreso en origen + ingreso en destino), vía RPC `crear_transferencia`.
   * La RPC valida RBAC, pertenencia de las cuentas a la org, cuentas activas,
   * origen ≠ destino y misma moneda. Devuelve el id del movimiento egreso.
   */
  async crearTransferencia(
    client: SupabaseClient<Database>,
    params: {
      organizacionId: string
      cuentaOrigenId: string
      cuentaDestinoId: string
      monto: number
      fecha: string
      observaciones?: string | null
    },
  ): Promise<string> {
    const { data, error } = await client.rpc('crear_transferencia', {
      p_organizacion_id: params.organizacionId,
      p_cuenta_origen_id: params.cuentaOrigenId,
      p_cuenta_destino_id: params.cuentaDestinoId,
      p_monto: params.monto,
      p_fecha: params.fecha,
      p_observaciones: params.observaciones ?? undefined,
    })
    if (error) throw error
    return data as string
  },

  /**
   * Registra un pago y lo aplica a deudas en UNA sola transacción, vía RPC
   * `registrar_pago_con_aplicaciones` (ORR-260). Antes eran dos requests —INSERT del
   * movimiento e INSERT de las aplicaciones— y PostgREST abre una transacción por
   * request: si la segunda fallaba, quedaba un pago grabado sin aplicar a nada.
   *
   * La RPC valida RBAC, que cliente y cuenta sean de la organización, que cada deuda
   * sea del mismo cliente y que los montos entren en el saldo REAL de cada deuda.
   * Si un saldo cambió desde que se abrió el diálogo, aborta entero (no recalcula) y
   * el error trae el saldo real. Con `aplicaciones` vacío registra un pago a cuenta.
   * Devuelve el id del pago creado.
   */
  async registrarPagoConAplicaciones(
    client: SupabaseClient<Database>,
    params: {
      organizacionId: string
      clienteId: string
      monto: number
      fecha: string
      concepto: string
      cuentaId: string
      servicioId?: string | null
      mesAnio?: string | null
      observaciones?: string | null
      createdBy?: string | null
      aplicaciones: AplicacionPago[]
    },
  ): Promise<string> {
    const { data, error } = await client.rpc('registrar_pago_con_aplicaciones', {
      p_organizacion_id: params.organizacionId,
      p_cliente_id: params.clienteId,
      p_monto: params.monto,
      p_fecha: params.fecha,
      p_concepto: params.concepto,
      p_cuenta_id: params.cuentaId,
      p_servicio_id: params.servicioId ?? undefined,
      p_mes_anio: params.mesAnio ?? undefined,
      p_observaciones: params.observaciones ?? undefined,
      p_created_by: params.createdBy ?? undefined,
      p_aplicaciones: params.aplicaciones.map(a => ({ deuda_id: a.deuda_id, monto: a.monto })),
    })
    if (error) throw error
    return data as string
  },

  async obtenerPorCliente(
    client: SupabaseClient<Database>,
    organizacionId: string,
    clienteId: string,
  ): Promise<Movimiento[]> {
    if (!organizacionId) throw new Error('No organization')
    const { data, error } = await client
      .from('movimientos')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .eq('cliente_id', clienteId)
      .order('fecha', { ascending: false })

    if (error) throw error
    return data as Movimiento[]
  },

  async listarCuentas(client: SupabaseClient<Database>, organizacionId: string): Promise<Cuenta[]> {
    const { data, error } = await client
      .from('cuentas')
      .select('*')
      .eq('activa', true)
      .or(cuentaOrgFilter(organizacionId))
      .order('nombre')

    if (error) throw error
    return data as Cuenta[]
  },

  // Crear múltiples movimientos (ej: honorarios mensuales masivos)
  async crearMasivo(client: SupabaseClient<Database>, movimientos: MovimientoInsert[]): Promise<Movimiento[]> {
    const { data, error } = await client
      .from('movimientos')
      .insert(movimientos)
      .select()

    if (error) throw error
    return data as Movimiento[]
  },

  // Clientes de la organización que ya tienen deuda MENSUAL en el período
  async obtenerClientesConMovimiento(
    client: SupabaseClient<Database>,
    organizacionId: string,
    mesAnio: string,
  ): Promise<string[]> {
    if (!organizacionId) throw new Error('No organization')
    const { data, error } = await client
      .from('movimientos')
      .select('cliente_id')
      .eq('organizacion_id', organizacionId)
      .eq('mes_anio', mesAnio)
      .eq('tipo', 'Deuda')
      .eq('tipo_deuda', 'mensual')

    if (error) throw error
    const movimientos = (data ?? []) as { cliente_id: string }[]
    return [...new Set(movimientos.filter(m => m.cliente_id).map(m => m.cliente_id))]
  },

  // Exportar movimientos a CSV
  exportarCSV(movimientos: MovimientoConCliente[], context?: ExportContext): string {
    const headers = [
      'Fecha',
      'Cliente',
      'CUIT',
      'Concepto',
      'Tipo',
      'Tipo Deuda',
      'Servicio',
      'Cuenta',
      'Debe',
      'Pendiente',
      'Haber',
      'Saldo',
      'Período',
      'Observaciones',
      'Estado',
    ]

    // Calculate accumulated balance per client
    const saldoPorMovimiento = calcularSaldosAcumulados(movimientos, undefined, undefined, context?.neto ?? false)

    let sumDebe = 0
    let sumPendiente = 0
    let sumHaber = 0

    const rows = movimientos.map(m => {
      const cliente = m.cliente as { persona?: { nombre_completo?: string; cuit?: string } } | null
      const cuitRaw = cliente?.persona?.cuit ?? ''
      const cuitFormatted = cuitRaw ? `="${formatearCUIT(cuitRaw)}"` : ''
      const debe = m.debe ?? 0
      const haber = m.haber ?? 0
      const saldo = saldoPorMovimiento.get(m.id) ?? 0
      // Saldo pendiente solo aplica a deudas; viene enriquecido desde
      // vista_saldos_deudas. Si nunca se aplicó un pago, fallback al debe.
      const pendiente = m.tipo === 'Deuda'
        ? (m._aplicacionSaldoPendiente ?? debe)
        : 0

      sumDebe += debe
      sumPendiente += pendiente
      sumHaber += haber

      return [
        m.fecha ? formatDate(m.fecha) : '',
        cliente?.persona?.nombre_completo ?? '',
        cuitFormatted,
        m.concepto ?? '',
        m.tipo ?? '',
        m.tipo_deuda ?? '',
        context?.serviciosMap?.get(m.servicio_id ?? '') ?? '',
        context?.cuentasMap?.get(m.cuenta_id ?? '') ?? '',
        debe.toFixed(2),
        pendiente > 0 ? pendiente.toFixed(2) : '',
        haber.toFixed(2),
        saldo.toFixed(2),
        m.mes_anio ?? '',
        m.observaciones ?? '',
        m._aplicacionEstado ?? m.estado ?? '',
      ]
    })

    // Totals row. En estado de cuenta (neto) el saldo total replica el footer
    // del dialog (calcularTotales: deudas/pagos parciales por su pendiente),
    // así el CSV coincide con lo que se ve en pantalla incluso con el filtro
    // "Solo pendientes" activo (ORR-153). Default: saldo bruto debe - haber.
    const saldoFinal = context?.neto ? calcularTotales(movimientos).saldo : sumDebe - sumHaber
    const totalsRow = [
      'TOTALES',
      '', '', '', '', '', '', '',
      sumDebe.toFixed(2),
      sumPendiente.toFixed(2),
      sumHaber.toFixed(2),
      saldoFinal.toFixed(2),
      '', '', '',
    ]

    // BOM para Excel reconozca UTF-8
    const BOM = '\uFEFF'
    const csv = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';')),
      totalsRow.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'),
    ].join('\n')

    return BOM + csv
  },

  // Descargar CSV
  descargarCSV(movimientos: MovimientoConCliente[], context?: ExportContext, filename?: string): void {
    const csv = this.exportarCSV(movimientos, context)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename ?? `movimientos_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  },
}
